import React, { useState, useEffect } from 'react';
import { Loader2, X as XIcon, Key, Shield, AlertTriangle, CheckCircle, ExternalLink, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';

/**
 * XOAuth1FallbackModal - Configure OAuth 1.0a fallback for X media uploads
 * 
 * New simplified flow:
 * 1. User enters API Key + API Secret only
 * 2. Click "Connecter" triggers OAuth 1.0a authorization
 * 3. User authorizes on X, redirected back
 * 4. Tokens are stored automatically
 */
export default function XOAuth1FallbackModal({ 
  isOpen, 
  onClose, 
  projectId,
  onSaved 
}) {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState(null);
  
  // Only need API Key and Secret now
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [enabled, setEnabled] = useState(true);

  // Check current status on open
  useEffect(() => {
    if (isOpen && projectId) {
      checkStatus();
    }
  }, [isOpen, projectId]);

  // Check for OAuth callback success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth1_success') === 'true') {
      const screenName = params.get('screen_name');
      toast.success(`OAuth 1.0a connecté ! @${screenName}`);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      checkStatus();
      onSaved?.();
    }
    if (params.get('oauth1_error')) {
      const message = params.get('message') || 'Erreur OAuth 1.0a';
      toast.error(message);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkStatus = async () => {
    setChecking(true);
    try {
      const response = await apiClient.get(`/integrations/x/oauth1-fallback/status?project_id=${projectId}`);
      setStatus(response.data);
      setEnabled(response.data.oauth1_enabled);
    } catch (error) {
      console.error('Failed to check OAuth 1.0a status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleStartAuth = async () => {
    if (!consumerKey || !consumerSecret) {
      toast.error('API Key et API Secret requis');
      return;
    }

    setLoading(true);
    try {
      // Start OAuth 1.0a flow
      const response = await apiClient.post('/integrations/x/oauth1/start', {
        project_id: projectId,
        consumer_key: consumerKey,
        consumer_secret: consumerSecret
      });

      if (response.data.success && response.data.authorization_url) {
        toast.success('Redirection vers X pour autorisation...');
        // Redirect to X authorization
        window.location.href = response.data.authorization_url;
      } else {
        toast.error('Erreur lors du démarrage de l\'autorisation');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Erreur: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Supprimer la configuration OAuth 1.0a ?')) return;

    setLoading(true);
    try {
      await apiClient.delete(`/integrations/x/oauth1-fallback?project_id=${projectId}`);
      toast.success('Configuration OAuth 1.0a supprimée');
      setStatus(null);
      setConsumerKey('');
      setConsumerSecret('');
      onSaved?.();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isConfigured = status?.has_oauth1_fallback && status?.has_access_token;
  const isPending = status?.oauth1_status === 'pending_authorization';

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="oauth1-fallback-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Key className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Fallback OAuth 1.0a
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Autorisation simplifiée pour l'upload média
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Warning/Info Box */}
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-300">
                  Quand utiliser OAuth 1.0a ?
                </p>
                <p className="mt-1 text-amber-700 dark:text-amber-400/80">
                  Si l'upload d'images échoue avec votre plan X actuel. OAuth 1.0a permet souvent de contourner cette limite.
                </p>
              </div>
            </div>
          </div>

          {/* Current Status */}
          {checking ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Vérification...</span>
            </div>
          ) : isConfigured ? (
            <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-300">
                    OAuth 1.0a connecté
                    {status.screen_name && <span className="ml-1">(@{status.screen_name})</span>}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-400/80">
                    {status.oauth1_enabled ? 'Actif' : 'Désactivé'} 
                    {status.configured_at && ` - ${new Date(status.configured_at).toLocaleDateString('fr-FR')}`}
                  </p>
                </div>
              </div>
            </div>
          ) : isPending ? (
            <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                <div>
                  <p className="font-medium text-blue-800 dark:text-blue-300">
                    En attente d'autorisation
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-400/80">
                    Cliquez sur "Connecter" pour autoriser l'application sur X
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {/* Instructions */}
          {!isConfigured && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Comment obtenir les credentials ?
              </h3>
              <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
                <li>Allez sur le <a href="https://developer.x.com/en/portal/dashboard" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">X Developer Portal <ExternalLink className="w-3 h-3" /></a></li>
                <li>Sélectionnez votre App {">"} Keys and Tokens</li>
                <li>Copiez <strong>API Key</strong> et <strong>API Secret</strong></li>
                <li>Cliquez sur "Connecter" ci-dessous pour autoriser</li>
              </ol>
            </div>
          )}

          {/* Form - Only API Key and Secret */}
          {!isConfigured && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key (Consumer Key)
                </label>
                <input
                  type="password"
                  value={consumerKey}
                  onChange={(e) => setConsumerKey(e.target.value)}
                  placeholder="Entrez votre API Key"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="oauth1-consumer-key"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Secret (Consumer Secret)
                </label>
                <input
                  type="password"
                  value={consumerSecret}
                  onChange={(e) => setConsumerSecret(e.target.value)}
                  placeholder="Entrez votre API Secret"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="oauth1-consumer-secret"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="oauth1-enabled"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor="oauth1-enabled" className="text-sm text-gray-700 dark:text-gray-300">
                  Activer le fallback OAuth 1.0a pour l'upload média
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-gray-200 dark:border-gray-700">
          {isConfigured ? (
            <button
              onClick={handleDelete}
              disabled={loading}
              className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors text-sm font-medium"
              data-testid="oauth1-delete-btn"
            >
              Déconnecter
            </button>
          ) : (
            <div />
          )}
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors text-sm font-medium"
            >
              Fermer
            </button>
            
            {!isConfigured && (
              <button
                onClick={handleStartAuth}
                disabled={loading || !consumerKey || !consumerSecret}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl transition-colors text-sm font-medium flex items-center gap-2"
                data-testid="oauth1-connect-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Connecter OAuth 1.0a
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
